'use client'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { useLocale } from '@/components/LocaleContext'
import { getAuthCookie } from '@/lib/auth'

type NavLink = { label: string; href: string }

type HeaderMobileMenuProps = {
  navLinks: NavLink[]
  open: boolean
  onClose: () => void
  handleLogout: () => void
  count: number
}

export function HeaderMobileMenu({
  navLinks,
  open,
  onClose,
  count,
  handleLogout,
}: HeaderMobileMenuProps) {
  const [mounted, setMounted] = useState(false)
  const isLoggedIn = Boolean(getAuthCookie())
  const { locale, translatedData } = useLocale()

  useEffect(() => setMounted(true), [])

  if (!open) return null

  const menu = (
    <div
      className="gallium-header__mobile-menu-backdrop"
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
      aria-hidden="true"
    >
      <button
        type="button"
        className="gallium-header__mobile-menu-overlay"
        aria-label={translatedData?.common.close_menu ?? 'Close menu'}
        onClick={onClose}
      />
      <div
        className="gallium-header__mobile-menu"
        role="dialog"
        aria-label={translatedData?.common.mobile_navigation ?? 'Mobile navigation'}
      >
        <ul className="gallium-header__mobile-nav">
          {navLinks.map(({ label, href }) => (
            <li key={label}>
              <Link
                href={href}
                onClick={onClose}
                className="gallium-header__mobile-nav-link text-left rtl:text-right"
              >
                {label}
              </Link>
            </li>
          ))}
          {isLoggedIn && (
            <li>
              <Link
                href="/dashboard"
                onClick={onClose}
                className="gallium-header__mobile-nav-link text-left rtl:text-right"
              >
                {translatedData?.common.dashboard}
              </Link>
            </li>
          )}
          <li>
            <Link
              href={isLoggedIn ? '/dealer-cart' : '/enquiry-cart'}
              onClick={onClose}
              className="gallium-header__mobile-nav-link text-left rtl:text-right"
            >
              {translatedData?.common.cart} {count > 0 && `(${count})`}
            </Link>
          </li>
          {isLoggedIn ? (
            <li>
              <button
                type="button"
                className="gallium-header__mobile-nav-btn text-left rtl:text-right"
                onClick={handleLogout}
              >
                {translatedData?.common.logout}
              </button>
            </li>
          ) : (
            <li>
              <Link
                href="/login"
                onClick={onClose}
                className="gallium-header__mobile-nav-link text-left rtl:text-right"
              >
                {translatedData?.common.login}
              </Link>
            </li>
          )}
        </ul>
      </div>
    </div>
  )

  if (mounted && typeof document !== 'undefined') {
    return createPortal(menu, document.body)
  }
  return menu
}
